import { useEffect, useMemo, useState } from 'react';
import { Alert } from '../../atoms/Alert';
import { Button } from '../../atoms/Button';
import Checkbox from '../../atoms/Checkbox';
import { Typography } from '../../molecules/Typography';
import { Modal } from '../../templates/Modal';
import { toast } from '../../atoms/Toast';

export interface SimuladoPrintItem {
  id: string;
  label: string;
  description?: string;
  /** PDF diagramado cadastrado no admin. Ausente = item indisponível. */
  url?: string | null;
  /** Nome do arquivo baixado. Cai no id quando não informado. */
  filename?: string;
  /** Motivo mostrado ao aluno quando o item não pode ser marcado. */
  unavailableReason?: string;
}

interface SimuladoPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  items: SimuladoPrintItem[];
  /**
   * Impressão gerada a partir da página, usada quando o simulado não tem os
   * PDFs diagramados cadastrados. Sem ela, um simulado antigo ficaria sem
   * nenhuma opção de impressão.
   */
  onFallbackPrint?: () => void;
  fallbackLabel?: string;
}

// Chrome e Safari descartam downloads disparados no mesmo tique. Um intervalo
// curto entre eles é suficiente para os três arquivos chegarem.
const DOWNLOAD_INTERVAL_MS = 600;

const triggerDownload = (url: string, filename: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

/**
 * Modal de impressão do simulado no formato fixo.
 *
 * Lista o que pode ser impresso — prova de cada dia e gabarito — com seleção
 * independente, e baixa os PDFs diagramados que o time pedagógico cadastra no
 * admin, em vez de gerar uma versão da própria página.
 */
export const SimuladoPrintModal = ({
  isOpen,
  onClose,
  title = 'Imprimir',
  subtitle,
  items,
  onFallbackPrint,
  fallbackLabel = 'Gerar versão simplificada da página',
}: SimuladoPrintModalProps) => {
  const availableItems = useMemo(
    () => items.filter((item) => !!item.url),
    [items]
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Reabrir o modal recomeça a seleção com tudo que está disponível marcado —
  // é o caso mais comum, e desmarcar é mais rápido do que marcar um a um.
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(availableItems.map((item) => item.id));
    }
  }, [isOpen, availableItems]);

  const toggle = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const handleDownload = async () => {
    const selected = availableItems.filter((item) =>
      selectedIds.includes(item.id)
    );

    if (!selected.length) return;

    setIsDownloading(true);

    try {
      selected.forEach((item, index) => {
        window.setTimeout(() => {
          triggerDownload(
            item.url as string,
            item.filename || `${item.id}.pdf`
          );
        }, index * DOWNLOAD_INTERVAL_MS);
      });

      toast({
        variant: 'success',
        title:
          selected.length > 1
            ? 'Downloads iniciados'
            : 'Download iniciado',
        message:
          selected.length > 1
            ? `${selected.length} arquivos serão baixados em seguida.`
            : 'O arquivo será baixado em instantes.',
        position: 'top-right',
        autoClose: 4000,
      });

      onClose();
    } catch (e) {
      toast({
        variant: 'error',
        title: 'Não foi possível baixar',
        message: 'Tente novamente em alguns instantes.',
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const hasAnyPdf = availableItems.length > 0;

  return (
    <Modal
      position='center'
      size='md'
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      className='xs:w-[90%] md:w-[60%] lg:w-[46%]'
    >
      <div className='flex flex-col gap-xl'>
        {!hasAnyPdf ? (
          <Alert
            variant='warning'
            message={
              <Typography size='default'>
                Este simulado ainda não tem os PDFs no modelo Enem cadastrados.
                {onFallbackPrint
                  ? ' Você pode gerar a versão simplificada da página.'
                  : ''}
              </Typography>
            }
          />
        ) : (
          <>
            <Typography size='default' className='text-mono-color-dark'>
              Selecione o que deseja imprimir. Os arquivos seguem a diagramação
              oficial da prova.
            </Typography>

            <ul className='flex flex-col gap-md'>
              {items.map((item) => {
                const isAvailable = !!item.url;

                return (
                  <li
                    key={item.id}
                    className='flex flex-col gap-xs rounded-md border-hairline border-mono-color-lighter p-4'
                  >
                    <Checkbox
                      id={item.id}
                      label={item.label}
                      checked={selectedIds.includes(item.id)}
                      disabled={!isAvailable}
                      onChange={() => toggle(item.id)}
                    />
                    {item.description && isAvailable && (
                      <Typography size='small' className='pl-9 text-mono-color-dark'>
                        {item.description}
                      </Typography>
                    )}
                    {!isAvailable && (
                      <Typography size='small' className='pl-9 text-mono-color-dark'>
                        {item.unavailableReason ??
                          'PDF ainda não disponível para este simulado.'}
                      </Typography>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className='flex flex-col gap-sm'>
          {hasAnyPdf && (
            <Button
              full
              LeftIcon='download'
              iconType='material'
              onClick={handleDownload}
              loading={isDownloading}
              disabled={!selectedIds.length || isDownloading}
            >
              {selectedIds.length > 1
                ? `Baixar ${selectedIds.length} arquivos`
                : 'Baixar arquivo'}
            </Button>
          )}

          {onFallbackPrint && (
            <Button
              full
              variant={hasAnyPdf ? 'text' : 'contained'}
              LeftIcon='print'
              iconType='material'
              onClick={onFallbackPrint}
            >
              {fallbackLabel}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SimuladoPrintModal;
